import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { getVisibleGroups, ADMIN_ITEMS, DASHBOARD_ITEM, MI_TRABAJO_ITEM } from "@/lib/navigation";
import { getRecents, pushRecent } from "@/lib/recents";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import {
  Sun, Moon, LogOut, FolderKanban, Scale, Briefcase, FileSearch,
  ClipboardList, Clock, Loader2, History, Compass,
} from "lucide-react";

const RESULT_CONFIG = [
  { key: "licitaciones",     icon: Scale,        labelKey: "cmdk.licitaciones", title: (d) => d.titulo,  sub: (d) => [d.municipio, d.estado_crm].filter(Boolean).join(" · "), path: (d) => `/licitaciones/${d.id}` },
  { key: "projects",         icon: FolderKanban, labelKey: "cmdk.projects",     title: (d) => d.title,   sub: (d) => [d.area, d.responsible_tech].filter(Boolean).join(" · "), path: (d) => `/projects/${d.id}` },
  { key: "offers",           icon: Briefcase,    labelKey: "cmdk.offers",       title: (d) => d.title,   sub: (d) => d.municipality, path: (d) => `/offers/${d.id}` },
  { key: "ofertas_comercial",icon: FileSearch,   labelKey: "cmdk.vsm",          title: (d) => d.objeto_contrato || d.expediente, sub: (d) => [d.cliente, d.estado_licitacion].filter(Boolean).join(" · "), path: () => "/ofertas-comercial" },
  { key: "checklists",       icon: ClipboardList,labelKey: "cmdk.checklists",   title: (d) => d.title,   sub: (d) => d.project, path: () => "/checklists" },
  { key: "horas_proyectos",  icon: Clock,        labelKey: "cmdk.horas",        title: (d) => d.nombre,  sub: (d) => d.cliente, path: () => "/horas" },
];

const TYPE_ICONS = {
  licitaciones: Scale,
  projects: FolderKanban,
  offers: Briefcase,
  ofertas_comercial: FileSearch,
  checklists: ClipboardList,
  horas_proyectos: Clock,
  nav: Compass,
};

export default function CommandPalette({ open, setOpen }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [recents, setRecents] = useState([]);
  const timerRef = useRef(null);
  const queryRef = useRef(query);
  useEffect(() => { queryRef.current = query; }, [query]);

  // Cargar recientes al abrir
  useEffect(() => {
    if (open) setRecents(getRecents(user?.email));
  }, [open, user]);

  // Global Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setOpen]);

  useEffect(() => {
    if (!open) { setQuery(""); setResults(null); }
  }, [open]);

  // Debounced backend search
  useEffect(() => {
    clearTimeout(timerRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search/global`, { params: { q } });
        // Descartar respuestas obsoletas si la query cambió mientras estaba en vuelo
        if (q === queryRef.current.trim()) setResults(res.data);
      } catch { setResults(null); }
      finally { setSearching(false); }
    }, 280);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const navItems = useMemo(() => {
    const groups = getVisibleGroups(user);
    const items = [DASHBOARD_ITEM, MI_TRABAJO_ITEM, ...groups.flatMap((g) => g.items)];
    if (user?.role === "admin") items.push(...ADMIN_ITEMS);
    return items;
  }, [user]);

  const ql = query.trim().toLowerCase();
  const filteredNav = ql
    ? navItems.filter((i) => t(i.label).toLowerCase().includes(ql))
    : navItems;

  const go = (path) => { setOpen(false); navigate(path); };
  const goRemember = (path, entry) => {
    pushRecent(user?.email, entry);
    go(path);
  };

  const totalResults = results
    ? RESULT_CONFIG.reduce((acc, c) => acc + (results[c.key]?.length || 0), 0)
    : 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder={t("cmdk.placeholder")}
        value={query}
        onValueChange={setQuery}
        data-testid="cmdk-input"
      />
      <CommandList className="max-h-[440px]" data-testid="cmdk-list">
        {searching && (
          <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
            <Loader2 size={13} className="animate-spin" /> {t("cmdk.searching")}
          </div>
        )}
        {!searching && filteredNav.length === 0 && totalResults === 0 && (
          <CommandEmpty>{t("cmdk.empty")}</CommandEmpty>
        )}

        {/* Recientes — solo sin query */}
        {!ql && recents.length > 0 && (
          <CommandGroup heading={t("cmdk.recents")} data-testid="cmdk-recents-group">
            {recents.map((r, i) => {
              const Icon = TYPE_ICONS[r.type] || History;
              return (
                <CommandItem
                  key={`recent-${i}`}
                  value={`recent-${i}-${r.path}`}
                  onSelect={() => go(r.path)}
                  data-testid={`cmdk-recent-${i}`}
                  className="gap-3"
                >
                  <Icon size={14} className="text-[#007934] dark:text-emerald-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{r.title}</p>
                    {r.sub && <p className="truncate text-xs text-muted-foreground">{r.sub}</p>}
                  </div>
                  <History size={11} className="text-muted-foreground/50 shrink-0" />
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {results && totalResults > 0 && RESULT_CONFIG.map((cfg) => {
          const docs = results[cfg.key] || [];
          if (!docs.length) return null;
          const Icon = cfg.icon;
          return (
            <CommandGroup key={cfg.key} heading={t(cfg.labelKey)}>
              {docs.map((d, i) => (
                <CommandItem
                  key={`${cfg.key}-${d.id || i}`}
                  value={`${cfg.key}-${d.id || i}`}
                  onSelect={() => goRemember(cfg.path(d), { path: cfg.path(d), title: cfg.title(d), sub: cfg.sub(d), type: cfg.key })}
                  data-testid={`cmdk-result-${cfg.key}-${i}`}
                  className="gap-3"
                >
                  <Icon size={14} className="text-[#007934] dark:text-emerald-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{cfg.title(d) || "—"}</p>
                    {cfg.sub(d) && <p className="truncate text-xs text-muted-foreground">{cfg.sub(d)}</p>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}

        {filteredNav.length > 0 && (
          <CommandGroup heading={t("cmdk.navigation")}>
            {filteredNav.slice(0, ql ? 8 : 30).map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.to}
                  value={`nav-${item.to}`}
                  onSelect={() => goRemember(item.to, { path: item.to, title: t(item.label), type: "nav" })}
                  data-testid={`cmdk-nav-${item.to.replace(/\//g, "")}`}
                  className="gap-3"
                >
                  <Icon size={14} className="text-muted-foreground shrink-0" />
                  <span className="text-sm">{t(item.label)}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        <CommandSeparator />
        <CommandGroup heading={t("cmdk.actions")}>
          <CommandItem value="action-theme" onSelect={() => { toggleTheme(); setOpen(false); }} className="gap-3" data-testid="cmdk-action-theme">
            {theme === "dark" ? <Sun size={14} className="shrink-0" /> : <Moon size={14} className="shrink-0" />}
            <span className="text-sm">{theme === "dark" ? t("cmdk.lightMode") : t("cmdk.darkMode")}</span>
          </CommandItem>
          <CommandItem value="action-logout" onSelect={() => { setOpen(false); logout(); navigate("/login"); }} className="gap-3" data-testid="cmdk-action-logout">
            <LogOut size={14} className="shrink-0" />
            <span className="text-sm">{t("cmdk.logout")}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
